import { format, addDays } from 'date-fns';
import {
  useEffect,
  useCallback,
} from 'react';

import { useWidgetState } from './use-widget-state';

/**
 * A custom hook for `Lodging` tab type (handling specifically iQ-Webbook found on Stovepipe Wells)
 * @returns {CustomTabState} a map of state and state update functions for all `Lodging` tabs
 */
export const useiQWebbookLodging = ({
  commerceEngineUrl,
  lodgingDepartureOffest,
}) => {
  const {
    tabValues,
    setTabValues,
    buttonLink,
    setButtonLink,
  } = useWidgetState();

  const components = [
    {
      name: 'arrivalDate',
      ariaLabel: 'Select check-in date',
      type: 'datepicker',
      isDefaultDate: false,
      isCurrentDaySelectable: true,
      placeholder: 'Arrive',
      maxDaysToBookAhead: 550,
      prettyDate: 'MMM dd yyyy',
      dateFormat: 'MM/dd/yyyy',
    },
    {
      name: 'departureDate',
      ariaLabel: 'Select departure date',
      type: 'datepicker',
      isDefaultDate: false,
      isCurrentDaySelectable: false,
      placeholder: 'Depart',
      maxDaysToBookAhead: 550,
      prettyDate: 'MMM dd yyyy',
      dateFormat: 'MM/dd/yyyy',
    },
    {
      name: 'adultsCount',
      ariaLabel: 'Select number of adult guests',
      type: 'select',
      placeholder: '# of Adults',
      defaultValue: { value: 1, label: '1 Adult' },
      choices: [
        { value: 0, label: 'No Adults' },
        { value: 1, label: '1 Adult' },
        { value: 2, label: '2 Adults' },
        { value: 3, label: '3 Adults' },
        { value: 4, label: '4 Adults' },
        { value: 5, label: '5 Adults' },
        { value: 6, label: '6 Adults' },
        { value: 7, label: '7 Adults' },
        { value: 8, label: '8 Adults' },
        { value: 9, label: '9 Adults' },
      ],
    },
    {
      name: 'childrenCount',
      ariaLabel: 'Select number of child guests',
      type: 'select',
      placeholder: '# of Children',
      defaultValue: { value: 0, label: 'No Children' },
      choices: [
        { value: 0, label: 'No Children' },
        { value: 1, label: '1 Child' },
        { value: 2, label: '2 Children' },
        { value: 3, label: '3 Children' },
        { value: 4, label: '4 Children' },
        { value: 5, label: '5 Children' },
        { value: 6, label: '6 Children' },
        { value: 7, label: '7 Children' },
        { value: 8, label: '8 Children' },
        { value: 9, label: '9 Children' },
      ],
    },
  ];

  useEffect(() => {
    const queries = [];

    if (tabValues[components[0].name]) {
      const { selectedDate, minDate } = tabValues[components[0].name];
      const dateString = format(selectedDate || minDate, 'EEE, MMM d yyyy');
      queries.push(`${components[0].name}=${encodeURIComponent(dateString)}`);
    }

    if (tabValues[components[0].name] && tabValues[components[1].name]) {
      const {
        minDate: minDepartureDate,
        selectedDate: selectedDepartureDate,
      } = tabValues[components[1].name];
      queries.push(
        `${components[1].name}=${
          encodeURIComponent(
            format(selectedDepartureDate || minDepartureDate, 'EEE, MMM d yyyy'),
          )
        }`,
      );
    }
    if (tabValues[components[2].name] && tabValues[components[3].name]) {
      queries.push(`rooms=[{${encodeURIComponent(`"${components[2].name}":${tabValues[components[2].name].value}, "${components[3].name}":${tabValues[components[3].name].value}`)}}]`);
    }

    if (
      tabValues[components[0].name]
      && tabValues[components[1].name]
      && tabValues[components[2].name]
      && tabValues[components[3].name]
    ) {
      const updatedQueryString = queries.join('&');
      setButtonLink(`${commerceEngineUrl}?${updatedQueryString}`);
    }
  }, [tabValues]);

  const customSetTabValues = useCallback((update) => {
    const updatedTabValues = { ...update };

    // Set the min date of the departureDate field to the selected date of the arrivalDate field
    if (updatedTabValues[components[0].name]?.selectedDate) {
      updatedTabValues[components[1].name] = {
        ...(tabValues[components[1].name] ?? {}),
        isAutoSelected: true,
        minDate: addDays(
          updatedTabValues[components[0].name].selectedDate,
          lodgingDepartureOffest || 1,
        ),
      };
    }

    setTabValues(updatedTabValues);
  }, [tabValues]);

  // set the default values for adult/child fields
  if (tabValues[components[2].name] === undefined) {
    tabValues[components[2].name] = {
      ...components[2].defaultValue,
    };
  }

  if (tabValues[components[3].name] === undefined) {
    tabValues[components[3].name] = {
      ...components[3].defaultValue,
    };
  }

  return {
    tabValues,
    setTabValues: customSetTabValues,
    buttonLink,
    components,
  };
};
